import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { TranslocoDirective } from '@jsverse/transloco';
import { TranslocoDecimalPipe } from '@jsverse/transloco-locale';
import { MatIconModule } from '@angular/material/icon';
import {
  AbstractModalComponent,
  BaseModalComponent,
  ButtonComponent,
  JunoCheckbox,
} from '@dss-libs/shared/ui/common-components';
import { InitialsPipe } from '@dss-libs/shared/ui/common-pipes';
import { OrdersService } from '@dss-libs/portal/data-access/contract-order';
import { EventTypeEnum, IRecipient } from '@dss-libs/shared/type/common-types';
import { SnackbarService } from '@dss-libs/shared/utility/common-services';
import { TSendReminderModalData } from '@dss-libs/portal/type/portal-types';
import { finalize } from 'rxjs/internal/operators/finalize';

@Component({
  selector: 'dss-libs-feature-send-reminder-modal',
  imports: [
    BaseModalComponent,
    ButtonComponent,
    MatIconModule,
    TranslocoDirective,
    JunoCheckbox,
    InitialsPipe,
    TranslocoDecimalPipe,
  ],
  templateUrl: './send-reminder-modal.component.html',
  styleUrl: './send-reminder-modal.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SendReminderModalComponent extends AbstractModalComponent<TSendReminderModalData> {
  readonly #contractOrderService = inject(OrdersService);
  readonly #snackbarService = inject(SnackbarService);

  protected readonly isLoading = signal(false);
  protected readonly selectedIds = signal(new Set<string>());
  protected readonly selectedCount = computed(() => this.selectedIds().size);

  protected isDisabled(recipient: IRecipient): boolean {
    return !!recipient.completedAt || !recipient.applicableToBeSentReminder;
  }

  protected isSelected(id: string): boolean {
    return this.selectedIds().has(id);
  }

  protected toggleRecipient(id: string, recipient: IRecipient): void {
    if (this.isDisabled(recipient)) return;
    this.selectedIds.update((ids) => {
      const next = new Set(ids);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  protected sendReminders(): void {
    const selectedRecipientIds = Array.from(this.selectedIds());
    if (selectedRecipientIds.length === 0) return;
    this.isLoading.set(true);
    this.#contractOrderService
      .sendReminders(this.dialogData!.orderId, selectedRecipientIds)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: () => {
          this.#snackbarService.showToaster(EventTypeEnum.SUCCESS, 'CONTRACT_ORDER.REMINDERS_SENT_SUCCESSFULLY');
          this.dialogRef.close();
        },
        error: () => {
          this.#snackbarService.showToaster(EventTypeEnum.ERROR, 'CONTRACT_ORDER.ORDER_WAS_CANCELLED_OR_DECLINED');
        },
      });
  }
}
